import React, { createContext, useContext, useReducer, useCallback } from 'react';
import { translateBackendMessage } from '../../utils/backendMessages';

const MessageContext = createContext();

const messageReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_MESSAGE':
      return {
        ...state,
        messages: [...state.messages, {
          id: action.payload.id,
          type: action.payload.type,
          title: action.payload.title,
          message: action.payload.message,
          duration: action.payload.duration,
          timestamp: Date.now()
        }]
      };
    case 'REMOVE_MESSAGE':
      return {
        ...state,
        messages: state.messages.filter(msg => msg.id !== action.payload.id)
      };
    case 'CLEAR_MESSAGES':
      return {
        ...state,
        messages: []
      };
    default:
      return state;
  }
};

const initialState = {
  messages: []
};

export const MessageProvider = ({ children }) => {
  const [state, dispatch] = useReducer(messageReducer, initialState);

  const addMessage = useCallback((type, title, message, duration = 5000) => {
    const id = Date.now() + Math.random();

    dispatch({
      type: 'ADD_MESSAGE',
      payload: { id, type, title, message: translateBackendMessage(message), duration }
    });

    // Auto-remove message after duration
    if (duration > 0) {
      setTimeout(() => {
        dispatch({
          type: 'REMOVE_MESSAGE',
          payload: { id }
        });
      }, duration);
    }

    return id;
  }, []);

  const removeMessage = useCallback((id) => {
    dispatch({
      type: 'REMOVE_MESSAGE',
      payload: { id }
    });
  }, []);

  const clearMessages = useCallback(() => {
    dispatch({ type: 'CLEAR_MESSAGES' });
  }, []);

  // Convenience methods for different message types
  const showSuccess = useCallback((title, message, duration) => {
    return addMessage('success', title, message, duration);
  }, [addMessage]);

  const showError = useCallback((title, message, duration = 8000) => {
    return addMessage('error', title, message, duration);
  }, [addMessage]);

  const showWarning = useCallback((title, message, duration) => {
    return addMessage('warning', title, message, duration);
  }, [addMessage]);

  const showInfo = useCallback((title, message, duration) => {
    return addMessage('info', title, message, duration);
  }, [addMessage]);

  const value = {
    messages: state.messages,
    addMessage,
    removeMessage,
    clearMessages,
    showSuccess,
    showError,
    showWarning,
    showInfo
  };

  return (
    <MessageContext.Provider value={value}>
      {children}
    </MessageContext.Provider>
  );
};

export const useMessages = () => {
  const context = useContext(MessageContext);
  if (!context) {
    throw new Error('useMessages must be used within a MessageProvider');
  }
  return context;
};

export default MessageContext;